/**
 * Pushes the dev-tools flag overrides into PostHog using the SDK's
 * official `overrideFeatureFlags` API. PostHog's hook (`useFeatureFlagEnabled`)
 * subscribes to flag changes internally, so this re-evaluates active hooks
 * without a reload.
 *
 * Call `initPostHogOverrideBridge()` once after PostHog has been initialized.
 * It is a no-op outside of `window` (e.g. SSR) and idempotent — repeated
 * calls reuse the existing subscription.
 *
 * Production safety: callers must gate on `getEnvironment() !== ENV_NAME.PROD`.
 * The bridge itself doesn't check the env so it can be unit-tested cheaply.
 *
 * API shape note (posthog-js >= 1.150 / current 1.362):
 *   `overrideFeatureFlags` ONLY persists overrides when called with one of
 *     - `false`                                     -> clears all overrides
 *     - `{ flags: ..., suppressWarning?: boolean }` -> registers `$override_feature_flags`
 *   A flat `Record<string, boolean>` is silently dropped (the function falls
 *   through to `_fireFeatureFlagsCallbacks` without writing to persistence).
 *   Do NOT "simplify" the call back to a flat record.
 *
 * Gotcha: `NEXT_PUBLIC_FORCE_ENABLE_ALL_FEATURE_FLAGS=true` monkey-patches
 * `posthog.isFeatureEnabled` and `posthog.getFeatureFlag` in
 * `src/components/constants/flags.ts` so they always return truthy values.
 * That bypasses the override registered here — `getFlagVariants()` will
 * reflect the override but app code reading via `isFeatureEnabled` won't.
 * Unset the env var if you're testing "force off" with this helper.
 */
import posthog from 'posthog-js';

import {
  getOverridesForProvider,
  getVariantOverride,
  subscribeToOverrides,
} from '@/features/flags-dev/overrides';
import { logger } from '@/utils/logger/logger';

let unsubscribe: (() => void) | undefined;

type PostHogInternals = {
  __loaded?: boolean;
  persistence?: unknown;
  featureFlags?: {
    overrideFeatureFlags?: (
      options:
        | false
        | { flags: Record<string, string | boolean>; suppressWarning?: boolean }
    ) => void;
  };
};

const applyOverrides = () => {
  try {
    const overrides = getOverridesForProvider('posthog');
    const phMap: Record<string, string | boolean> = {};
    for (const [key, value] of Object.entries(overrides)) {
      phMap[key] = getVariantOverride(value) ?? value === 'on';
    }
    const ph = posthog as unknown as PostHogInternals;
    const featureFlags = ph.featureFlags;
    if (!featureFlags || typeof featureFlags.overrideFeatureFlags !== 'function') {
      return;
    }
    // overrideFeatureFlags silently bails inside PostHog if these aren't
    // set yet, so just no-op until init has completed.
    if (ph.__loaded !== true || !ph.persistence) return;

    if (Object.keys(phMap).length === 0) {
      featureFlags.overrideFeatureFlags(false);
    } else {
      // `suppressWarning: true` silences PostHog's "Overriding feature
      // flags!" console warning, which would otherwise fire on every
      // callback dispatch while any override is active.
      featureFlags.overrideFeatureFlags({
        flags: phMap,
        suppressWarning: true,
      });
    }
  } catch (err) {
    logger.warn('[flags-dev] PostHog override push failed', { error: String(err) });
  }
};

export const initPostHogOverrideBridge = (): void => {
  if (typeof window === 'undefined') return;
  if (unsubscribe) return;
  unsubscribe = subscribeToOverrides(applyOverrides);
  applyOverrides();
};

export const teardownPostHogOverrideBridge = (): void => {
  unsubscribe?.();
  unsubscribe = undefined;
};
